import { Box, Divider, IconButton, Typography } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import type { PurchaseCostFormValues } from "./purchaseCostFormSchema"

import { Button } from "@/components/common/Button"
import { FormAutocomplete, FormDatePicker, FormNumericField, FormSelect } from "@/components/UiKit/FormControls"
import { useGetWarehouseOptions } from "@/hooks/useGetWarehouseOptions"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { selectProduct, useProductStore } from "@/stores/useProductStore"
import type { Option } from "@/types/form-fields"
import { createUomsList } from "@/utils/conversion"

interface PurchaseCostStep1FormProps {
  isWarehouseLocked: boolean
}

export const PurchaseCostStep1Form: FC<PurchaseCostStep1FormProps> = ({ isWarehouseLocked }) => {
  const { control, getValues, setValue } = useFormContext<PurchaseCostFormValues>()
  const { fields, append, remove } = useFieldArray<PurchaseCostFormValues, "tiers">({
    control,
    name: "tiers",
  })

  const { warehouseOptions } = useGetWarehouseOptions({ isActive: true, isInventoryRelated: true })
  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const product = useProductStore(selectProduct)

  const uomOptions = useMemo<Option<string>[]>(() => {
    const filtered = product ? createUomsList(units, product) : units
    return filtered.map((unit) => ({ value: unit.id, label: unit.name ?? unit.description ?? unit.id }))
  }, [units, product])

  const handleAdditionalTier = () => {
    const sharedUomId = getValues("tiers.0.uomId") ?? ""
    append({ uomId: sharedUomId, qty: null, price: null })
  }

  const handleTierUomChange = (value: string | null) => {
    const nextUomId = value ?? ""
    getValues("tiers").forEach((_, i) => {
      setValue(`tiers.${i}.uomId`, nextUomId, { shouldDirty: true, shouldValidate: true })
    })
  }

  return (
    <Box className='flex flex-col gap-y-6'>
      <FormAutocomplete
        control={control}
        name='warehouseAreaId'
        label='Warehouse'
        required
        placeholder='Select warehouse'
        options={warehouseOptions}
        disabled={isWarehouseLocked}
        data-testid='product.purchase-costs.form-dialog.warehouse-field'
      />

      <Box className='flex flex-col gap-y-4'>
        {fields.map((field, index) => (
          <Box key={field.id} className={`flex flex-col gap-y-6 ${index > 0 ? "mt-4" : ""}`}>
            <Box className='flex flex-col gap-y-2'>
              <Box className='flex items-center justify-between'>
                <Typography variant='subtitle2' className='text-gray-800'>
                  {index === 0 ? (
                    <>
                      Price Tier 1<span className='text-red-600'> *</span>
                    </>
                  ) : (
                    <>Price Tier {index + 1}</>
                  )}
                </Typography>
                {index > 0 && (
                  <IconButton
                    size='small'
                    onClick={() => remove(index)}
                    data-testid={`product.purchase-costs.form-dialog.tier-remove-button-${index}`}
                  >
                    <DeleteOutlined />
                  </IconButton>
                )}
              </Box>
              <Divider />
            </Box>
            <Box className='grid grid-cols-3 gap-x-3'>
              <FormSelect
                control={control}
                name={`tiers.${index}.uomId`}
                label='Pricing UoM'
                required
                placeholder='Select type'
                options={uomOptions}
                onChange={handleTierUomChange}
                data-testid={`product.purchase-costs.form-dialog.tier-uom-field-${index}`}
              />
              <FormNumericField
                control={control}
                name={`tiers.${index}.qty`}
                label='Product Quantity'
                required
                placeholder='Input quantity'
                allowNegative={false}
                data-testid={`product.purchase-costs.form-dialog.tier-qty-field-${index}`}
              />
              <FormNumericField
                control={control}
                name={`tiers.${index}.price`}
                label='Currency/Price per Unit'
                required
                placeholder='Input price per'
                allowNegative={false}
                decimalScale={2}
                data-testid={`product.purchase-costs.form-dialog.tier-price-field-${index}`}
              />
            </Box>
          </Box>
        ))}

        <Box className='flex justify-end'>
          <Button
            appearance='text'
            size='small'
            startIcon={<AddOutlined />}
            onClick={handleAdditionalTier}
            data-testid='product.purchase-costs.form-dialog.additional-tier-button'
          >
            Additional Tier
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box className='grid grid-cols-2 gap-x-3'>
        <FormDatePicker
          control={control}
          name='effectiveDate'
          label='Effective Date'
          required
          slotProps={{ textField: { placeholder: "Add date" } }}
          data-testid='product.purchase-costs.form-dialog.effective-date-field'
        />
        <FormDatePicker
          control={control}
          name='expirationDate'
          label='Valid Until Date'
          slotProps={{ textField: { placeholder: "Add date" } }}
          data-testid='product.purchase-costs.form-dialog.expiration-date-field'
        />
      </Box>
    </Box>
  )
}
